import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { CreatorProfile } from '@/components/shared/CreatorBadge';

const fallbackProfile = (uid: string): CreatorProfile => ({
  uid,
  displayName: uid ? `${uid.slice(0, 8)}...` : 'Unknown',
  email: null,
  photoURL: null,
  avatarUrl: null,
});

export function useCreatorProfiles(creatorIds: Array<string | undefined | null>) {
  const creatorKey = useMemo(() => {
    return Array.from(new Set(creatorIds.filter((uid): uid is string => Boolean(uid))))
      .sort()
      .join('|');
  }, [creatorIds]);

  const [profiles, setProfiles] = useState<Map<string, CreatorProfile>>(new Map());

  useEffect(() => {
    if (!creatorKey) {
      setProfiles(new Map());
      return;
    }

    const creatorUids = creatorKey.split('|');
    let cancelled = false;

    const fetchProfiles = async () => {
      const entries = await Promise.all(
        creatorUids.map(async (uid): Promise<[string, CreatorProfile]> => {
          try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (!userDoc.exists()) {
              return [uid, fallbackProfile(uid)];
            }

            const data = userDoc.data();
            return [
              uid,
              {
                uid,
                email: data.email || null,
                displayName: data.displayName || data.email || fallbackProfile(uid).displayName,
                photoURL: data.photoURL || data.avatarUrl || null,
                avatarUrl: data.avatarUrl || data.photoURL || null,
              },
            ];
          } catch {
            return [uid, fallbackProfile(uid)];
          }
        })
      );

      if (cancelled) return;
      setProfiles(new Map(entries));
    };

    fetchProfiles();

    return () => {
      cancelled = true;
    };
  }, [creatorKey]);

  return profiles;
}
